import { styled } from "styled-components";

// Styled Components
const SelectContainer = styled.div`
  width: 100%;
  position: relative;
`;

const SelectWrapper = styled.div<{
  $borderColor?: string;
  $bgColor?: string;
  $width?: number;
  $height?: number;
  $borderRadius?: number;
  $marginLeftMinus?: number;
  $disabled?: boolean;
}>`
  width: 100%;
  max-width: ${(props) => props.$width}px;
  min-height: ${(props) => props.$height}px;
  border: 1px solid ${(props) => props.$borderColor || "#d9d9d9"};
  border-radius: ${(props) => props.$borderRadius || 6}px;
  background-color: ${(props) => props.$bgColor || "#ffffff"};
  margin-left: ${(props) =>
    props.$marginLeftMinus ? `-${props.$marginLeftMinus}px` : "0"};
  position: relative;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${(props) =>
      !props.$disabled ? props.$borderColor || "#40a9ff" : props.$borderColor};
  }

  &:focus-within {
    border-color: ${(props) => props.$borderColor || "#40a9ff"};
    box-shadow: 0 0 0 2px ${(props) => props.$borderColor || "#40a9ff"}20;
  }
`;

const SelectContent = styled.div<{
  $fontSize?: number;
  $fontWeight?: number;
  $color?: string;
  $height?: number;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 11px;
  min-height: ${(props) => (props.$height ? `${props.$height - 2}px` : "30px")};
  font-size: ${(props) => props.$fontSize}px;
  font-weight: ${(props) => props.$fontWeight};
  color: ${(props) => props.$color};
  overflow: hidden;
`;

const SelectedItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
  align-items: center;
`;

const SelectedTag = styled.div<{
  $color?: string;
}>`
  background-color: #76b041;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 14px;
  color: ${(props) => props.$color || "#FEFAE3"};
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 150px;
  height: 38px;

  &:hover {
    background-color: #e39435;
  }
`;

const SelectedTagText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #fefae3;
  font-size: 16px;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Placeholder = styled.div<{
  $labelColor?: string;
  $labelFontSize?: number;
  $labelFontWeight?: number;
}>`
  color: ${(props) => props.$labelColor || "#b0b0b0"};
  font-size: ${(props) =>
    props.$labelFontSize ? `${props.$labelFontSize}px` : "16px"};
  font-weight: ${(props) => props.$labelFontWeight || 500};
  user-select: none;
`;

const ArrowIcon = styled.div<{
  $isOpen?: boolean;
  $color?: string;
}>`
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 4px solid ${(props) => props.$color || "#bfbfbf"};
  transition: transform 0.3s ease;
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  margin-left: 8px;
`;

const DropdownContainer = styled.div<{
  $isOpen?: boolean;
  $maxHeight?: number;
}>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 1000;
  max-height: ${(props) => props.$maxHeight || 200}px;
  overflow-y: auto;
  display: ${(props) => (props.$isOpen ? "block" : "none")};
  margin-top: 4px;
`;

const SearchInput = styled.input<{
  $fontSize?: number;
  $color?: string;
}>`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  outline: none;
  font-size: ${(props) => props.$fontSize}px;
  color: ${(props) => props.$color};

  &::placeholder {
    color: #bfbfbf;
  }
`;

const OptionsList = styled.div`
  max-height: 150px;
  overflow-y: auto;
`;

const OptionItem = styled.div<{
  $isSelected?: boolean;
  $isHighlighted?: boolean;
  $menuItemColor?: string;
}>`
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) =>
    props.$isSelected ? "#E39435" : props.$menuItemColor || "#1E2F23"};
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const CheckIcon = styled.div<{
  $isVisible?: boolean;
}>`
  width: 12px;
  height: 12px;
  opacity: ${(props) => (props.$isVisible ? 1 : 0)};
  color: #e39435;
  font-weight: bold;
  &::after {
    content: "✓";
  }
`;

const NoDataText = styled.div`
  padding: 16px 12px;
  text-align: center;
  color: #bfbfbf;
  font-size: 14px;
`;

// Export all styled components
export {
  SelectContainer,
  SelectWrapper,
  SelectContent,
  SelectedItems,
  SelectedTag,
  SelectedTagText,
  RemoveButton,
  Placeholder,
  ArrowIcon,
  DropdownContainer,
  SearchInput,
  OptionsList,
  OptionItem,
  CheckIcon,
  NoDataText,
};
